import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  ChevronRight, 
  Clock, 
  Calendar, 
  ChevronDown, 
  ChevronUp,
  CheckCircle2,
  Lightbulb,
  ArrowRight,
  FileText,
  Target,
  Layers,
  Construction,
  Wrench,
  Settings,
  Gauge,
  AlertTriangle,
  Cog,
  Activity,
  Ruler,
  ExternalLink,
  BarChart3,
  TrendingUp,
  CircleDot,
  Waves,
  LineChart,
  FlaskConical,
  HelpCircle,
  BookOpen,
  Microscope
} from 'lucide-react';

const PressiometricTestGuide = () => {
  const [activeSection, setActiveSection] = useState('');
  const [openFaq, setOpenFaq] = useState(null);

  // Table of contents
  const sections = [
    { id: 'section-1', title: "Qu'est-ce que l'essai pressiométrique ?" },
    { id: 'section-2', title: "La norme en vigueur" },
    { id: 'section-3', title: "Les trois paramètres mesurés" },
    { id: 'section-4', title: "Comment se déroule un essai" },
    { id: 'section-5', title: "Lire une courbe pressiométrique" },
    { id: 'section-6', title: "À quoi sert le pressiomètre ?" },
    { id: 'section-7', title: "Questions fréquentes" },
    { id: 'section-8', title: "Matériel et fiabilité" },
  ];

  const faqs = [
    {
      question: "Quelle différence entre pressiomètre et pénétromètre ?",
      answer: "Le pressiomètre se réalise dans un forage préalable et mesure la réaction du sol à une expansion latérale — il donne à la fois déformabilité (EM) et résistance (pl*). Le pénétromètre, lui, enfonce une pointe dans le sol et mesure la résistance à la pénétration. Les deux sont complémentaires : le pressiomètre est plus riche en information, le pénétromètre plus rapide et continu."
    },
    {
      question: "Quelle est la norme applicable aujourd'hui ?",
      answer: "La NF EN ISO 22476-4, deuxième édition de septembre 2021, qui a remplacé l'ancienne NF P 94-110-1."
    },
    {
      question: "Que signifie un EM élevé ?",
      answer: "Plus le module pressiométrique EM est élevé, plus le sol est rigide (il se déforme peu sous la charge). Un EM faible signale un terrain compressible, donc un risque de tassements plus important."
    },
    {
      question: "La qualité du forage influence-t-elle le résultat ?",
      answer: "Énormément. Un forage mal exécuté (parois remaniées, diamètre inadapté, décompression) fausse la mesure. C'est pourquoi la norme et les guides pratiques du projet ARSCOP insistent tant sur les bonnes pratiques de mise en place de la sonde. Le savoir-faire du sondeur est déterminant."
    },
    {
      question: "L'essai peut-il se faire dans la roche ?",
      answer: "L'essai s'applique aux sols naturels, aux couches traitées ou non traitées et aux roches fragiles, sur terre comme en milieu maritime."
    }
  ];

  // Track scroll for active section
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150;
      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    }
  };

  // Schema.org JSON-LD
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Essai pressiométrique Ménard : EM, pression limite et interprétation | Guide",
    "description": "Comprendre l'essai pressiométrique Ménard : principe, norme NF EN ISO 22476-4, paramètres EM, pl* et pf, lecture de la courbe et dimensionnement des fondations.",
    "image": "https://geots.consulting/og-images/guide-essai-pressiometrique.jpg",
    "author": {
      "@type": "Organization",
      "name": "GEOTS Consulting"
    },
    "publisher": {
      "@type": "Organization",
      "name": "GEOTS Consulting",
      "logo": {
        "@type": "ImageObject",
        "url": "https://geots.consulting/logo.png"
      }
    },
    "datePublished": "2026-07-19",
    "dateModified": "2026-07-19"
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  // Parameters table data
  const parameters = [
    { 
      param: "EM", 
      name: "Module pressiométrique (MPa)", 
      measures: "La déformabilité du sol : plus EM est élevé, plus le sol est rigide", 
      usage: "Calcul des tassements des fondations",
      icon: TrendingUp,
      color: "bg-blue-50 border-blue-200"
    },
    { 
      param: "pl*", 
      name: "Pression limite nette (MPa)", 
      measures: "La résistance à la rupture : la pression au-delà de laquelle le sol cède", 
      usage: "Calcul de la portance des semelles et des pieux",
      icon: Gauge,
      color: "bg-green-50 border-green-200"
    },
    { 
      param: "pf", 
      name: "Pression de fluage (MPa)", 
      measures: "La frontière entre comportement élastique et plastique du sol", 
      usage: "Qualification de l'état du sol, contrôle qualité de l'essai",
      icon: Activity,
      color: "bg-purple-50 border-purple-200"
    },
  ];

  // Test steps
  const testSteps = [
    { 
      number: 1, 
      title: "Le forage", 
      desc: "On réalise un forage soigné, à la profondeur d'essai voulue. La qualité de ce préforage est déterminante — un trou mal calibré ou remanié fausse tout l'essai.", 
      icon: Cog 
    },
    { 
      number: 2, 
      title: "La descente de la sonde", 
      desc: "La sonde pressiométrique (à trois cellules : une cellule centrale de mesure encadrée de deux cellules de garde) est descendue à la profondeur d'essai.", 
      icon: Ruler 
    },
    { 
      number: 3, 
      title: "Le chargement par paliers", 
      desc: "On augmente la pression par paliers successifs, en relevant à chaque fois le volume injecté (donc la déformation du sol).", 
      icon: TrendingUp 
    },
    { 
      number: 4, 
      title: "L'enregistrement", 
      desc: "Le contrôleur pression-volume, aujourd'hui souvent numérique, enregistre les données. Les afficheurs modernes offrent une lecture plus fine que les manomètres analogiques.", 
      icon: BarChart3 
    },
    { 
      number: 5, 
      title: "L'exploitation", 
      desc: "On trace la courbe pressiométrique, dont on déduit EM, pf et pl*. L'essai est répété à différentes profondeurs pour établir un profil complet.", 
      icon: LineChart 
    },
  ];

  // Curve phases
  const curvePhases = [
    { 
      phase: "Phase 1", 
      name: "La recompression", 
      desc: "La sonde reprend contact avec le terrain remanié par le forage. Cette phase ne caractérise pas le sol intact.",
      color: "bg-gray-100 border-gray-300"
    },
    { 
      phase: "Phase 2", 
      name: "La phase pseudo-élastique", 
      desc: "La courbe devient une droite. C'est sa pente qui donne EM, le module pressiométrique.",
      color: "bg-blue-50 border-blue-300"
    },
    { 
      phase: "Phase 3", 
      name: "La phase plastique", 
      desc: "Le sol se déforme de plus en plus pour un même incrément de pression. La courbe s'infléchit puis diverge. C'est cette phase qui conduit à pl*, la pression limite.",
      color: "bg-green-50 border-green-300"
    },
  ];

  // Use cases
  const useCases = [
    { title: "Identifier les couches", desc: "Succession des couches de terrain et leur nature", icon: Layers },
    { title: "Dimensionner les fondations", desc: "Portance des semelles et pieux, estimation des tassements", icon: Construction },
    { title: "Choisir le type de fondation", desc: "Superficielle ou profonde, profondeur d'ancrage", icon: Target },
    { title: "Vérifier un terrain traité", desc: "Colonnes ballastées, inclusions rigides", icon: CheckCircle2 },
    { title: "Alimenter les missions G1/G2", desc: "Conformément à la norme NF P 94-500", icon: FileText },
  ];

  return (
    <>
      <Helmet>
        <title>Essai pressiométrique Ménard : EM, pression limite et interprétation | Guide</title>
        <meta name="description" content="Comprendre l'essai pressiométrique Ménard : principe, norme NF EN ISO 22476-4, paramètres EM, pl* et pf, lecture de la courbe et dimensionnement des fondations." />
        <link rel="canonical" href="https://geots.consulting/guides/essai-pressiometrique-menard" />
        <meta property="og:title" content="Essai pressiométrique Ménard : EM, pression limite et interprétation | Guide" />
        <meta property="og:description" content="Comprendre l'essai pressiométrique Ménard : principe, norme NF EN ISO 22476-4, paramètres EM, pl* et pf, lecture de la courbe et dimensionnement des fondations." />
        <meta property="og:url" content="https://geots.consulting/guides/essai-pressiometrique-menard" />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-[#0A2540] via-[#0A2540] to-[#1a3a5c] text-white">
          <div className="container mx-auto px-4 py-12 md:py-20">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm mb-8 text-gray-300">
              <Link to="/" className="hover:text-white">Accueil</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/guides" className="hover:text-white">Guides</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-white">Essai pressiométrique</span>
            </nav>

            <div className="max-w-4xl">
              <div className="inline-flex items-center gap-2 bg-[#635BFF]/20 text-[#00D4FF] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Gauge className="w-4 h-4" />
                Guide technique
              </div>
              
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
                L'essai pressiométrique Ménard : guide complet
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#00D4FF] to-[#635BFF] mt-2">
                  (EM, pl*, interprétation)
                </span>
              </h1>

              <p className="text-xl text-gray-300 mb-8 leading-relaxed max-w-3xl">
                C'est l'essai in situ le plus utilisé sur les chantiers français, et la base du dimensionnement 
                de la plupart des fondations. Le pressiomètre Ménard mesure d'un seul coup la déformabilité et 
                la résistance d'un sol, directement dans le terrain. Comment ça marche, que signifient EM et pl*, 
                et comment lire un profil pressiométrique ? Ce guide fait le tour.
              </p>

              <div className="flex flex-wrap items-center gap-6 text-sm text-gray-400">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>Mis à jour : Juillet 2026</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>Temps de lecture : 10 min</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-12">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Sidebar - Table of Contents */}
            <aside className="lg:w-72 flex-shrink-0">
              <div className="lg:sticky lg:top-24">
                <div className="bg-white rounded-xl shadow-lg border p-6">
                  <h3 className="font-bold text-[#0A2540] mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[#635BFF]" />
                    Sommaire
                  </h3>
                  <nav className="space-y-1">
                    {sections.map((section, index) => (
                      <button
                        key={section.id}
                        onClick={() => scrollToSection(section.id)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all flex items-center gap-2 ${
                          activeSection === section.id
                            ? 'bg-[#635BFF]/10 text-[#635BFF] font-medium'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <span className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center text-xs font-medium">
                          {index + 1}
                        </span>
                        <span className="line-clamp-2">{section.title}</span>
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Related Guides */}
                <div className="bg-gradient-to-br from-[#635BFF]/5 to-[#00D4FF]/5 rounded-xl p-6 mt-6 border border-[#635BFF]/10">
                  <h4 className="font-bold text-[#0A2540] mb-4">Guides connexes</h4>
                  <div className="space-y-3">
                    <Link to="/guides/comment-choisir-etude-geotechnique" className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#635BFF] transition-colors">
                      <ArrowRight className="w-4 h-4" />
                      Choisir son étude géotechnique
                    </Link>
                    <Link to="/guides/materiel-de-forage" className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#635BFF] transition-colors">
                      <ArrowRight className="w-4 h-4" />
                      Matériel de forage complet
                    </Link>
                    <Link to="/guides/micropieux" className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#635BFF] transition-colors">
                      <ArrowRight className="w-4 h-4" />
                      Micropieux : guide complet
                    </Link>
                    <Link to="/guides/comment-choisir-fondations-speciales" className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#635BFF] transition-colors">
                      <ArrowRight className="w-4 h-4" />
                      Choisir ses fondations spéciales
                    </Link>
                  </div>
                </div>
              </div>
            </aside>

            {/* Main Article */}
            <article className="flex-1 max-w-3xl">
              {/* Section 1: What is it? */}
              <section id="section-1" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-[#635BFF]/10 flex items-center justify-center">
                    <Microscope className="w-5 h-5 text-[#635BFF]" />
                  </span>
                  Qu'est-ce que l'essai pressiométrique Ménard ?
                </h2>

                <div className="prose prose-lg max-w-none text-gray-700">
                  <p>
                    L'essai pressiométrique est un <strong>essai de chargement réalisé directement dans le sol</strong>, 
                    au fond d'un forage. Le principe : on descend une sonde cylindrique dans un trou préalablement foré, 
                    puis on la <strong>gonfle progressivement</strong> contre les parois. En mesurant, pour chaque palier 
                    de pression, de combien le terrain se déforme, on obtient une relation directe entre pression appliquée 
                    et déformation du sol.
                  </p>

                  <div className="bg-gradient-to-r from-[#635BFF]/5 to-[#00D4FF]/5 rounded-xl p-6 my-6 border-l-4 border-[#635BFF]">
                    <p className="font-medium text-[#0A2540] mb-2 flex items-center gap-2">
                      <Lightbulb className="w-5 h-5 text-[#635BFF]" />
                      L'immense avantage
                    </p>
                    <p className="text-gray-700 mb-0">
                      Cette méthode mesure le comportement du sol <strong>en place</strong>, dans son état naturel, 
                      sans avoir à prélever un échantillon qui serait forcément remanié pendant le transport et la manipulation.
                    </p>
                  </div>

                  <p>
                    L'essai a été mis au point dans les années 1950 par <strong>Louis Ménard</strong>, ingénieur des 
                    Ponts et Chaussées, qui dépose le brevet du pressiomètre en 1955. Sa méthode, capable de donner 
                    en un seul essai la déformabilité <strong>et</strong> la résistance du terrain, est devenue une 
                    référence française reprise dans de nombreux pays.
                  </p>
                </div>
              </section>

              {/* Section 2: The standard */}
              <section id="section-2" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-blue-600" />
                  </span>
                  La norme en vigueur
                </h2>

                <div className="bg-white rounded-xl p-6 border shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-8 h-8 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#0A2540] text-lg mb-2">NF EN ISO 22476-4</h3>
                      <p className="text-gray-600 mb-3">
                        Deuxième édition — Septembre 2021
                      </p>
                      <p className="text-gray-700 text-sm">
                        Cette norme a remplacé l'ancienne norme française NF P 94-110-1. Elle bénéficie des enseignements 
                        du <strong>projet national ARSCOP</strong> et apporte des évolutions notables sur la mise en place 
                        de la sonde, les systèmes de pilotage et d'enregistrement, ainsi que sur les méthodes d'interprétation.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 bg-gray-50 rounded-xl p-6 border">
                  <p className="text-gray-700">
                    L'essai s'inscrit dans le cadre de l'<strong>Eurocode 7</strong> pour la reconnaissance et le calcul géotechniques.
                  </p>
                </div>
              </section>

              {/* Section 3: Parameters */}
              <section id="section-3" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-green-600" />
                  </span>
                  Les trois paramètres mesurés
                </h2>

                <p className="text-gray-700 mb-6">
                  L'essai fournit trois grandeurs, qui sont le cœur de son intérêt :
                </p>

                <div className="space-y-4 mb-8">
                  {parameters.map((param, index) => {
                    const IconComponent = param.icon;
                    return (
                      <div 
                        key={index}
                        className={`rounded-xl p-5 border-2 ${param.color}`}
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-16 h-16 rounded-xl bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
                            <span className="text-2xl font-bold text-[#0A2540]">{param.param}</span>
                          </div>
                          <div className="flex-1">
                            <h3 className="font-bold text-[#0A2540] mb-1">{param.name}</h3>
                            <p className="text-gray-700 text-sm mb-2">{param.measures}</p>
                            <div className="flex items-center gap-2 text-sm">
                              <IconComponent className="w-4 h-4 text-[#635BFF]" />
                              <span className="text-gray-600 italic">{param.usage}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="bg-gradient-to-r from-[#635BFF]/5 to-[#00D4FF]/5 rounded-xl p-6 border-l-4 border-[#00D4FF]">
                  <p className="text-gray-700 mb-0">
                    Ces paramètres alimentent directement les méthodes françaises de dimensionnement des fondations : 
                    <strong> NF P 94-261</strong> (fondations superficielles) et <strong>NF P 94-262</strong> (fondations profondes).
                  </p>
                </div>
              </section>

              {/* Section 4: Test procedure */}
              <section id="section-4" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                    <Settings className="w-5 h-5 text-purple-600" />
                  </span>
                  Comment se déroule un essai
                </h2>

                <div className="space-y-6">
                  {testSteps.map((step, index) => {
                    const IconComponent = step.icon;
                    return (
                      <div 
                        key={index}
                        className="flex items-start gap-4 bg-white rounded-xl p-6 border shadow-sm"
                      >
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#635BFF] to-[#00D4FF] flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                          {step.number}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <IconComponent className="w-5 h-5 text-[#635BFF]" />
                            <h3 className="font-bold text-[#0A2540]">{step.title}</h3>
                          </div>
                          <p className="text-gray-600">{step.desc}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* Section 5: Reading the curve */}
              <section id="section-5" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
                    <LineChart className="w-5 h-5 text-orange-600" />
                  </span>
                  Lire une courbe pressiométrique
                </h2>

                <p className="text-gray-700 mb-6">
                  La courbe se lit en <strong>trois phases</strong> :
                </p>

                <div className="space-y-4 mb-8">
                  {curvePhases.map((phase, index) => (
                    <div 
                      key={index}
                      className={`rounded-xl p-5 border-2 ${phase.color}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-lg bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
                          <span className="text-lg font-bold text-[#0A2540]">{index + 1}</span>
                        </div>
                        <div>
                          <h3 className="font-bold text-[#0A2540] mb-1">{phase.phase} — {phase.name}</h3>
                          <p className="text-gray-700 text-sm">{phase.desc}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
                  <div className="flex items-start gap-3">
                    <Lightbulb className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-bold text-amber-800 mb-2">Le rapport EM/pl*</h4>
                      <p className="text-amber-700">
                        C'est un indicateur précieux : il renseigne sur la nature du terrain (les sols granulaires et 
                        les sols cohérents ne présentent pas les mêmes rapports) et permet de contrôler la cohérence de l'essai.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Section 6: Use cases */}
              <section id="section-6" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center">
                    <Target className="w-5 h-5 text-teal-600" />
                  </span>
                  À quoi sert le pressiomètre dans un projet ?
                </h2>

                <div className="grid md:grid-cols-2 gap-4 mb-8">
                  {useCases.map((use, index) => {
                    const IconComponent = use.icon;
                    return (
                      <div 
                        key={index}
                        className="bg-white rounded-xl p-5 border hover:border-[#635BFF]/50 hover:shadow-md transition-all"
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-[#635BFF]/10 flex items-center justify-center flex-shrink-0">
                            <IconComponent className="w-5 h-5 text-[#635BFF]" />
                          </div>
                          <div>
                            <h3 className="font-bold text-[#0A2540] mb-1">{use.title}</h3>
                            <p className="text-gray-600 text-sm">{use.desc}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="bg-gradient-to-r from-[#635BFF]/5 to-[#00D4FF]/5 rounded-xl p-6 border-l-4 border-[#635BFF]">
                  <p className="text-gray-700 mb-0 font-medium">
                    En pratique, comprendre l'essai pressiométrique, c'est comprendre comment la grande majorité 
                    des fondations françaises sont calculées.
                  </p>
                </div>

                {/* Cross-links */}
                <div className="mt-8 grid md:grid-cols-2 gap-4">
                  <Link 
                    to="/guides/comment-choisir-etude-geotechnique"
                    className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-xl hover:bg-blue-100 transition-colors"
                  >
                    <FileText className="w-6 h-6 text-blue-600" />
                    <div>
                      <p className="font-semibold text-blue-800">Études géotechniques</p>
                      <p className="text-sm text-blue-600">Missions G1 à G5</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-blue-600 ml-auto" />
                  </Link>
                  <Link 
                    to="/guides/micropieux"
                    className="flex items-center gap-3 p-4 bg-cyan-50 border border-cyan-200 rounded-xl hover:bg-cyan-100 transition-colors"
                  >
                    <CircleDot className="w-6 h-6 text-cyan-600" />
                    <div>
                      <p className="font-semibold text-cyan-800">Micropieux</p>
                      <p className="text-sm text-cyan-600">Types et dimensionnement</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-cyan-600 ml-auto" />
                  </Link>
                </div>
              </section>

              {/* Section 7: FAQ */}
              <section id="section-7" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                    <HelpCircle className="w-5 h-5 text-indigo-600" />
                  </span>
                  Questions fréquentes
                </h2>

                <div className="space-y-4">
                  {faqs.map((faq, index) => (
                    <div 
                      key={index}
                      className="bg-white rounded-xl border overflow-hidden"
                    >
                      <button
                        onClick={() => setOpenFaq(openFaq === index ? null : index)}
                        className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
                      >
                        <span className="font-semibold text-[#0A2540] pr-4">{faq.question}</span>
                        {openFaq === index ? (
                          <ChevronUp className="w-5 h-5 text-[#635BFF] flex-shrink-0" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        )}
                      </button>
                      {openFaq === index && (
                        <div className="px-5 pb-5 text-gray-600 border-t">
                          <p className="pt-4">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>

              {/* Section 8: Equipment */}
              <section id="section-8" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-[#635BFF]/10 flex items-center justify-center">
                    <Wrench className="w-5 h-5 text-[#635BFF]" />
                  </span>
                  Matériel et fiabilité de la mesure
                </h2>

                <div className="prose prose-lg max-w-none text-gray-700">
                  <p>
                    La validité d'un essai pressiométrique repose sur trois maillons : la <strong>qualité du forage</strong>, 
                    l'<strong>état du matériel</strong> (sonde, tubulures, contrôleur pression-volume) et son <strong>étalonnage</strong>. 
                    Un appareillage dérivé ou une sonde fatiguée produisent des courbes inexploitables — avec, à la clé, 
                    un dimensionnement de fondations basé sur de fausses valeurs.
                  </p>
                </div>

                {/* Cross-link to matériel de forage */}
                <div className="mt-6">
                  <Link 
                    to="/guides/materiel-de-forage"
                    className="flex items-center gap-3 p-4 bg-orange-50 border border-orange-200 rounded-xl hover:bg-orange-100 transition-colors"
                  >
                    <Wrench className="w-6 h-6 text-orange-600" />
                    <div>
                      <p className="font-semibold text-orange-800">Matériel de forage</p>
                      <p className="text-sm text-orange-600">Guide complet des équipements</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-orange-600 ml-auto" />
                  </Link>
                </div>

                {/* CTA Box */}
                <div className="mt-8 bg-gradient-to-br from-[#0A2540] to-[#1a3a5c] rounded-2xl p-8 text-white">
                  <h3 className="text-xl font-bold mb-4">
                    Besoin de matériel pressiométrique ?
                  </h3>
                  <p className="text-gray-300 mb-6">
                    GEOTS met en relation les acteurs de la géotechnique : bureaux d'études, entreprises de sondage, 
                    loueurs de matériel d'essai et d'instrumentation, spécialistes de l'étalonnage. 
                    Besoin de matériel pressiométrique, d'un étalonnage ou d'un prestataire pour vos essais in situ ?
                  </p>
                  <div className="flex flex-wrap gap-4">
                    <Link 
                      to="/contact"
                      className="inline-flex items-center gap-2 bg-[#635BFF] hover:bg-[#5248d9] text-white px-6 py-3 rounded-lg font-medium transition-colors"
                    >
                      Contactez-nous
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                    <Link 
                      to="/annuaire/ingenierie-etudes-geotechniques/essais-pressiometriques-penetrometriques"
                      className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                    >
                      Voir l'annuaire essais
                      <ExternalLink className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </section>

              {/* Disclaimer */}
              <div className="text-sm text-gray-500 italic border-t pt-8">
                Ce guide est fourni à titre informatif. La réalisation et l'interprétation des essais pressiométriques 
                doivent être confiées à des professionnels qualifiés, conformément à la norme NF EN ISO 22476-4.
              </div>
            </article>
          </div>
        </div>

        {/* Related Guides Section */}
        <div className="bg-gray-50 border-t py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-8 text-center">
              Continuez votre lecture
            </h2>
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              <Link 
                to="/guides/comment-choisir-etude-geotechnique"
                className="bg-white rounded-xl p-6 border hover:shadow-lg transition-shadow group"
              >
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mb-4">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-bold text-[#0A2540] mb-2 group-hover:text-[#635BFF] transition-colors">
                  Comment choisir son étude géotechnique ?
                </h3>
                <p className="text-gray-600 text-sm">
                  Missions G1 à G5, normes et quand les commander.
                </p>
              </Link>

              <Link 
                to="/guides/materiel-de-forage"
                className="bg-white rounded-xl p-6 border hover:shadow-lg transition-shadow group"
              >
                <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center mb-4">
                  <Wrench className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="font-bold text-[#0A2540] mb-2 group-hover:text-[#635BFF] transition-colors">
                  Matériel de forage : guide complet
                </h3>
                <p className="text-gray-600 text-sm">
                  Foreuses, outils et instrumentation.
                </p>
              </Link>

              <Link 
                to="/guides/micropieux"
                className="bg-white rounded-xl p-6 border hover:shadow-lg transition-shadow group"
              >
                <div className="w-12 h-12 rounded-xl bg-cyan-100 flex items-center justify-center mb-4">
                  <CircleDot className="w-6 h-6 text-cyan-600" />
                </div>
                <h3 className="font-bold text-[#0A2540] mb-2 group-hover:text-[#635BFF] transition-colors">
                  Micropieux : guide complet
                </h3>
                <p className="text-gray-600 text-sm">
                  Types, mise en œuvre et reprise en sous-œuvre.
                </p>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PressiometricTestGuide;
